import {Controller} from "@hotwired/stimulus"
import {placeFloatingPanel} from "../../lib/floating_panel"

// Dropdown menu for redesigned (ice-) screens.
//
//   %div{ data: { controller: 'ice-menu', 'ice-menu-placement-value': 'bottom-right auto' } }
//     %button{ data: { action: 'ice-menu#toggle' } } …
//     %div{ data: { 'ice-menu-target': 'panel' }, hidden: true }
//       -# links
//
// Unlike ice-popover, the contents are markup the caller wrote — links, forms,
// checkboxes — so nothing is built here. The controller only owns open/close and
// where the panel lands.
//
// The panel is moved to <body> while open. These menus live inside table cells, and
// a table row establishes enough of a clipping and stacking context that a panel
// left in place gets cut off no matter which way it opens. Fixed positioning alone
// is not always enough — Bootstrap's modal dialog carries a transform, and any
// transformed ancestor re-anchors `fixed`.
//
// State is named around Stimulus rather than against it: `scope` is a getter on
// Controller with no setter, so assigning to it throws in strict mode. Hence
// `mount` for the element we create, not `scope`.
export default class extends Controller {
    static targets = ["panel"]
    static values = {
        placement: {type: String, default: "bottom-right auto"}
    }

    connect() {
        this.isOpen = false
        this.placeholder = null
        this.panel = null

        this.onDocumentPointerDown = this.onDocumentPointerDown.bind(this)
        this.onKeydown = this.onKeydown.bind(this)
        this.reposition = this.reposition.bind(this)
    }

    disconnect() {
        this.close()
    }

    toggle(event) {
        event.preventDefault()
        event.stopPropagation()
        this.isOpen ? this.close() : this.show()
    }

    show() {
        if (this.isOpen || !this.hasPanelTarget) return

        // Hold the element, not the target. Stimulus resolves targets by searching
        // the controller's subtree, and the next few lines move the panel out of it —
        // from then on panelTarget throws "Missing target element", which is what
        // used to break close() and leave the menu stuck open.
        const panel = this.panelTarget
        this.panel = panel

        // A comment node marks the spot so the panel goes back exactly where it was —
        // the row may be re-rendered by a js.erb response while the menu is closed.
        this.placeholder = document.createComment("ice-menu panel")
        panel.parentNode.insertBefore(this.placeholder, panel)

        this.mount = document.createElement("div")
        this.mount.className = "ice:preflight ice:rem16-scale ice:fixed ice:z-popover"
        this.mount.appendChild(panel)
        document.body.appendChild(this.mount)

        panel.hidden = false
        this.isOpen = true
        this.setExpanded(true)

        this.reposition()

        window.addEventListener("scroll", this.reposition, {passive: true, capture: true})
        window.addEventListener("resize", this.reposition)
        document.addEventListener("pointerdown", this.onDocumentPointerDown)
        document.addEventListener("keydown", this.onKeydown)
    }

    close() {
        if (!this.isOpen) return

        window.removeEventListener("scroll", this.reposition, {capture: true})
        window.removeEventListener("resize", this.reposition)
        document.removeEventListener("pointerdown", this.onDocumentPointerDown)
        document.removeEventListener("keydown", this.onKeydown)

        const panel = this.panel
        if (panel) panel.hidden = true

        if (panel && this.placeholder && this.placeholder.parentNode) {
            this.placeholder.parentNode.insertBefore(panel, this.placeholder)
            this.placeholder.remove()
        }
        this.placeholder = null
        this.panel = null

        if (this.mount) {
            this.mount.remove()
            this.mount = null
        }

        this.isOpen = false
        this.setExpanded(false)
    }

    onDocumentPointerDown(event) {
        if (this.element.contains(event.target)) return
        if (this.mount && this.mount.contains(event.target)) return

        this.close()
    }

    onKeydown(event) {
        if (event.key === "Escape") this.close()
    }

    reposition() {
        if (!this.isOpen) return

        const trigger = this.element.querySelector("[data-action*='ice-menu#toggle']") || this.element

        placeFloatingPanel(this.mount, trigger.getBoundingClientRect(), {
            placement: this.placementValue
        })
    }

    setExpanded(state) {
        const trigger = this.element.querySelector("[aria-expanded]")
        if (trigger) trigger.setAttribute("aria-expanded", state ? "true" : "false")
    }
}
